/**
 * The authored one-line description of every entity type.
 *
 * WHY THIS LIVES IN THE SPEC PACKAGE (0.34.1). It used to live only in the
 * documentation site's generator, which is an application. That put the one
 * sentence explaining what a type IS somewhere no consumer of the format could
 * reach: `get_entity_schema` returned `type, domain, expected_properties,
 * edges_out, edges_in, domain_guide` and no description, and
 * `get_catalog_entry({ kind: 'entity_meta' })` returned `name, type_id,
 * maturity, since, domain_id` and no description. The MCP server's own
 * instructions say "Before creating an entity or edge, call `get_entity_schema`
 * for the type", and the sentence that answers the question the caller actually
 * has was the one thing that call did not return.
 *
 * It also meant 0.32.1's editorial gate — which turns a release red until every
 * type has an authored description, after 0.32.0 shipped `capture` with the
 * generator's fallback text — protected a surface no agent could see.
 *
 * PULLED FORWARD BY NECESSITY, and worth saying plainly: promoting these to a
 * public export was a banked item, not a planned part of this patch. There is no
 * other way for an agent surface to return an authored description, because the
 * only authored copy was in an app. The alternative was a second copy in this
 * package, and a second copy of a hand-maintained string table is the exact
 * defect the derived base-node field list exists to prevent.
 *
 * SINGLE SOURCE. The site generator now READS this table instead of holding its
 * own; `check:generated` proves the emitted site data is unchanged by the move.
 * A new entity type without an entry here is caught by `check:editorial`.
 *
 * Descriptions are prose for a human reader and carry no contract. The
 * normative statements about a type are its properties, its edges and its
 * lifecycle.
 */
export const UPG_ENTITY_DESCRIPTIONS: Readonly<Record<string, string>> = Object.freeze({
  // Strategy
  product: 'The product being created, the root of the graph',
  outcome: 'A desired business or user outcome',
  objective: 'A strategic goal (OKR)',
  key_result: 'A measurable result tied to an objective',
  metric: 'A unified metric that measures progress, health, or behaviour across the product',
  metric_quality_assessment: 'An assessment of whether a metric is well-defined, measurable, and aligned to a meaningful outcome.',
  vision: 'A long-term aspirational statement of the future state',
  mission: 'The purpose and reason the product exists',
  strategic_theme: 'A high-level strategic focus area for a planning period. Its own time_horizon is now deprecated: promote the period to a planning_cycle and link the two with strategic_theme_scoped_to_planning_cycle, so themes share one dated, nestable interval instead of a drifting per-theme label.',
  initiative: 'A large coordinated effort to achieve a strategic goal',
  capability: 'An ability that enables value delivery',
  value_stream: 'An end-to-end flow delivering value to the customer',
  strategic_pillar: 'A foundational principle that guides decisions',
  strategic_question: 'An open strategic question a plan must resolve (of coordination, ownership, sequencing, or which bet to place), held explicitly until a decision retires it; the strategy sibling of research_question and design_question',
  assumption: 'A belief taken as true that underpins a strategy',
  decision: 'A recorded decision with context, rationale, and consequences',
  constraint: 'A limit, requirement, or ceiling the product must respect, whether a self-imposed principle or an externally imposed boundary',
  // User
  persona: 'An archetype representing a user segment',
  job: 'Job To Be Done: what the user is trying to accomplish',
  need: 'A user need, pain, desire, or constraint',
  desired_outcome: 'What the user hopes to achieve',
  job_step: 'A discrete step within a Job To Be Done',
  switching_cost: 'A barrier preventing users from switching alternatives',
  // Discovery
  opportunity: 'A validated gap worth solving',
  solution: 'A proposed approach to address an opportunity',
  feasibility_study: 'An assessment of technical or business viability',
  design_sprint: 'A time-boxed sprint to prototype and validate ideas',
  // Validation
  hypothesis: 'A testable belief about a solution',
  experiment: 'A test designed to validate a hypothesis',
  experiment_plan: 'An experiment plan describing the hypothesis, setup, success criteria, and methodology before a test runs.',
  experiment_run: 'An execution instance of an experiment that records actual conditions, observations, and raw results.',
  learning: 'An insight gained from an experiment',
  test_plan: 'A structured plan for testing a hypothesis',
  evidence: 'Data supporting or refuting a hypothesis',
  research_plan: 'A plan for gathering evidence to validate a draft entity',
  // Market Intelligence
  competitor: 'A competing product or company',
  competitor_feature: 'A feature offered by a competitor',
  market_trend: 'An emerging trend in the market',
  market_segment: 'A distinct group of potential customers',
  competitive_analysis: 'A structured analysis of the competitive landscape',
  classification_axis: 'A single dimension in a competitive classification matrix that defines one axis of comparison across alternatives.',
  classification_value: 'A position or value on a classification axis, representing where one option lands on that dimension.',
  competitor_signal: 'An observed competitor move or datapoint (a pricing change, launch, or hire) that feeds competitive analysis',
  // User Research
  research_study: 'A planned research activity',
  insight: 'A synthesised finding from research',
  participant: 'A person participating in research',
  observation: 'A specific behaviour or statement observed',
  quote: 'A direct quote from a research participant',
  affinity_cluster: 'A group of related observations',
  research_question: 'A question guiding a research study',
  interview_guide: 'A structured guide for conducting interviews',
  survey_response: 'A response to a survey instrument',
  // Experience Design
  user_journey: 'An end-to-end map of a user experience',
  journey_step: 'A single step in a user journey',
  journey_phase: 'A named phase in a user journey that groups a sequence of actions the user performs to reach a milestone.',
  journey_action: 'A single step a user takes within a journey phase, the atomic unit of observable user behaviour.',
  user_flow: 'A navigation path through the product',
  screen: 'A distinct screen or view in the product',
  screen_state: 'A specific state of a screen (e.g., empty, loading, error)',
  surface: 'A place inside a screen, the features that occupy it, and the rule that decides who wins it',
  design_question: 'An open design problem to explore',
  design_concept: 'A possible design direction or approach',
  prototype: 'An interactive mockup for testing',
  wireframe: 'A low-fidelity structural layout',
  annotation: 'A note or markup on a design artifact',
  interaction_spec: 'A specification for an interaction behaviour',
  // Design System
  design_component: 'A reusable UI component',
  design_token: 'A design token (colour, spacing, typography)',
  design_system: 'The root design system entity',
  design_pattern: 'A documented design pattern',
  design_guideline: 'A design usage guideline',
  // Brand Identity
  brand_identity: 'The root brand identity entity',
  brand_colour: 'A brand colour definition',
  brand_typography: 'Brand typography specifications',
  brand_voice: 'Brand voice and tone guidelines',
  brand_logo: 'A brand logo or mark',
  brand_imagery: 'Brand imagery and photography guidelines',
  // Product Specification
  feature: 'A product capability or feature',
  feature_area: 'A grouping of related features',
  epic: 'A large body of work that can be broken into stories',
  user_story: 'A user\'s goal and the value they expect, in the "As a… I want… So that…" format. Now also a first-class plannable unit (priority, effort, assignee, due_date) that schedules into a planning_cycle, and it round-trips an external board\'s column via workflow_state.',
  acceptance_criterion: 'A condition that must be met for a story to be done',
  release: 'A shipped version of the product',
  task: 'A unit of work within a story or epic',
  bug: 'A defect or unexpected behaviour',
  roadmap: 'A strategic plan of features and milestones',
  roadmap_item: 'An item on the product roadmap',
  theme: 'A strategic grouping of related features',
  roadmap_theme: 'A customer problem used as the organising unit of a roadmap',
  changelog: 'A record of changes shipped in a release',
  planning_cycle: 'The cadence axis of a plan: a dated interval a team plans in, be it a sprint, iteration, quarter, or program increment. Cycles self-nest, so a fine iteration sits inside a coarse period to form a granularity ladder.',
  configuration_axis: 'A named dimension along which the product\'s composition differs: a feature flag, a plan tier, a permission level, a beta programme. The stored graph is the union of the family; one configuration is a projection of it.',
  // Engineering
  bounded_context: 'A DDD bounded context defining a service boundary',
  service: 'A deployable service or microservice',
  domain_event: 'An event published when something happens in the domain',
  api_contract: 'An API contract or specification',
  technical_debt_item: 'A known piece of technical debt',
  feature_flag: 'A feature toggle for controlled rollout',
  deployment: 'A deployment event',
  aggregate: 'A DDD aggregate root',
  domain_entity: 'A DDD domain entity: a core object with a distinct identity and lifecycle inside a bounded context',
  value_object: 'A DDD value object',
  command: 'A DDD command (write operation)',
  read_model: 'A DDD read model (query projection)',
  api_endpoint: 'A specific API endpoint',
  database_schema: 'A database schema definition',
  queue_topic: 'A message queue topic',
  build_artifact: 'A build output (binary, container image)',
  code_repository: 'A source code repository',
  library_dependency: 'A third-party library dependency',
  integration_pattern: 'An integration pattern (saga, event sourcing, etc.)',
  external_api: 'An external API consumed by the product',
  data_flow: 'A data flow between systems',
  investigation: 'An investigation into an issue or incident',
  root_cause: 'An identified root cause of an issue',
  symptom: 'A symptom of a problem',
  fix: 'A fix applied to resolve an issue',
  // Growth
  funnel: 'A conversion funnel tracking user progression',
  funnel_step: 'A stage within a conversion funnel',
  acquisition_channel: 'A channel through which users are acquired',
  growth_campaign: 'A growth-focused campaign',
  cohort: 'A group of users sharing a common characteristic',
  behavioral_segment: 'A user segment based on behaviour',
  growth_loop: 'A self-reinforcing growth cycle',
  variant: 'A variant in an A/B test',
  attribution_model: 'A model for attributing conversions to touchpoints',
  // Business Model
  business_model: 'The business model canvas or definition',
  value_proposition: 'A unique value offered to customers',
  revenue_stream: 'A source of revenue',
  pricing_tier: 'A pricing tier or plan',
  cost_structure: 'A cost category or structure',
  unit_economics: 'Per-unit economic metrics (CAC, LTV, etc.)',
  partnership: 'A strategic partnership',
  key_resource: 'A key resource required by the business',
  key_activity: 'A key activity the business performs',
  target_customer_segment: 'A target customer segment',
  customer_relationship: 'A type of customer relationship',
  distribution_channel: 'A channel for delivering value to customers',
  // Go-To-Market
  gtm_strategy: 'A go-to-market strategy',
  ideal_customer_profile: 'The ideal customer profile (ICP)',
  positioning: 'Product positioning statement',
  messaging: 'Messaging framework and key messages',
  launch: 'A product launch event',
  content_strategy: 'A content strategy for thought leadership',
  sales_motion: 'A repeatable sales motion',
  competitive_battle_card: 'A competitive battle card for sales enablement',
  demand_gen_program: 'A demand generation program',
  territory: 'A sales territory',
  objection: 'A common sales objection',
  rebuttal: 'A rebuttal to a sales objection',
  proof_point: 'Evidence supporting a sales claim',
  // Team & Organisation
  team: 'A cross-functional team',
  role: 'A role within a team',
  stakeholder: 'A person with influence over the product',
  team_okr: 'A team-level OKR',
  retrospective: 'A team retrospective',
  dependency: 'A cross-team or system dependency',
  department: 'An organisational department',
  skill: 'A skill or competency within a team',
  ceremony: 'A recurring team ritual (standup, planning)',
  capacity_plan: 'A plan for allocating team capacity',
  person: 'A named individual (owner, stakeholder, or participant) referenced across the graph',
  // Data & Analytics
  data_source: 'A data source or integration',
  event_schema: 'An event schema for tracking',
  dashboard: 'An analytics dashboard',
  data_model: 'A data model or schema',
  data_quality_rule: 'A data quality validation rule',
  data_product: 'A curated, reusable data asset',
  data_pipeline: 'An automated pipeline for data transformation',
  data_lineage: 'A record of data origin and transformations',
  glossary_term: 'A defined term for shared understanding',
  data_domain: 'A logical grouping of related data assets',
  report: 'A structured analytical report',
  // Customer Success
  support_ticket: 'Customer support request or issue',
  customer_feedback: 'Voice-of-customer input',
  churn_reason: 'Why a customer left',
  customer_health_score: 'A composite health score for a customer',
  playbook: 'A standard operating procedure for CS',
  service_level_agreement: 'A service level agreement with customers',
  customer_journey_stage: 'A stage in the post-sale customer journey',
  touchpoint: 'A customer interaction touchpoint',
  success_milestone: 'A customer success milestone',
  service_blueprint: 'A blueprint of the full service delivery',
  // Content & Knowledge
  content_piece: 'A piece of content (article, video, etc.)',
  knowledge_base_article: 'A knowledge base article',
  brand_asset: 'A brand asset (logo, image, etc.)',
  internal_doc: 'An internal documentation page',
  prompt_template: 'A reusable prompt template',
  content_calendar: 'A content publication calendar',
  content_theme: 'A thematic grouping for content',
  documentation_template: 'A template for documentation',
  document: 'A general-purpose document',
  // Legal
  legal_entity: 'A legal entity (company, subsidiary)',
  ip_asset: 'An intellectual property asset',
  contract: 'A legal contract',
  contract_clause: 'A clause within a contract',
  privacy_policy: 'A privacy policy',
  // Compliance
  compliance_requirement: 'A compliance requirement',
  risk: 'A risk to the product or business',
  data_contract: 'A data-sharing contract',
  audit_log_policy: 'An audit logging policy',
  compliance_framework: 'A compliance framework (SOC 2, GDPR, etc.)',
  security_audit: 'A security audit',
  // DevOps & Platform
  service_level_indicator: 'A service level indicator (SLI)',
  service_level_objective: 'A service level objective (SLO)',
  error_budget: 'An error budget for a service',
  incident: 'A production incident',
  postmortem: 'A post-incident review',
  runbook: 'A runbook for incident response',
  monitor: 'A monitoring check',
  alert_rule: 'An alerting rule',
  ci_pipeline: 'A CI/CD pipeline',
  release_strategy: 'A release strategy (canary, blue-green, etc.)',
  on_call_rotation: 'An on-call rotation schedule',
  infrastructure_component: 'An infrastructure component (server, CDN, etc.)',
  // Security
  threat_model: 'A threat model for the system',
  threat: 'A specific security threat',
  vulnerability: 'A known vulnerability',
  security_control: 'A security control or mitigation',
  security_policy: 'A security policy',
  penetration_test: 'A penetration test',
  security_review: 'A security review',
  data_classification: 'A data classification level',
  access_policy: 'An access control policy',
  // Accessibility
  a11y_standard: 'An accessibility standard (WCAG, Section 508)',
  a11y_guideline: 'An accessibility guideline',
  a11y_audit: 'An accessibility audit',
  a11y_issue: 'An accessibility issue',
  a11y_annotation: 'An accessibility annotation on a design',
  // Quality Assurance
  test_suite: 'A suite of related tests',
  test_case: 'An individual test case',
  qa_session: 'An exploratory QA session',
  regression_test: 'A regression test',
  test_coverage_report: 'A test coverage report',
  test_environment: 'A testing environment',
  test_result: 'A test execution result',
  // Customer Feedback
  feedback_program: 'A structured feedback collection program',
  feature_request: 'A user-submitted feature request',
  feedback_vote: 'A vote on a feature request or feedback item',
  nps_campaign: 'A Net Promoter Score survey campaign',
  user_advisory_board: 'A panel of users providing ongoing feedback',
  beta_program: 'A beta testing program',
  feedback_theme: 'A recurring theme across feedback items',
  // Pricing & Packaging
  pricing_strategy: 'An overarching pricing strategy',
  package: 'A bundled package of features at a price point',
  discount_strategy: 'A strategy for offering discounts',
  trial_config: 'Configuration for a free trial',
  paywall: 'A paywall gating premium features',
  // AI & Machine Learning
  ai_model: 'An AI or ML model used within the product',
  prompt_version: 'A version of a prompt template',
  eval_benchmark: 'A benchmark for evaluating AI quality',
  eval_run: 'An evaluation run against a benchmark',
  ai_cost_tracker: 'A tracker for AI inference costs',
  hallucination_report: 'A report of an AI hallucination',
  ai_guardrail: 'A guardrail for AI safety',
  model_comparison: 'A comparison between AI models',
  ai_experiment: 'An AI-focused experiment',
  ai_dataset: 'A dataset for AI training or evaluation',
  ai_trace: 'An AI inference trace',
  // Workflows & Agents
  workflow_template: 'A reusable workflow template',
  workflow_run: 'An execution of a workflow',
  agent_definition: 'An autonomous agent definition',
  agent_session: 'An agent working session',
  review_gate: 'A human review checkpoint in a workflow',
  approval_record: 'A record of an approval decision',
  agent_skill: 'A capability of an agent',
  agent_hook: 'A trigger connecting an agent to an event',
  workflow_artifact: 'An output artifact from a workflow run',
  agent_task: 'A discrete unit of agent work',
  // Portfolio
  organization: 'The top-level organisational entity',
  portfolio: 'A grouping of products by strategic axis',
  product_area: 'A grouping of products by organisational axis',
  // Sales & Revenue
  account: 'A customer account',
  contact: 'A person within an account',
  lead: 'An inbound lead',
  deal: 'An active sales opportunity',
  pipeline_sales: 'A sales pipeline',
  pipeline_stage: 'A stage in the sales pipeline',
  quote_document: 'A formal quote document',
  subscription: 'A recurring subscription',
  invoice: 'An invoice for billing',
  forecast: 'A revenue forecast',
  // Program Management
  program: 'A program coordinating multiple projects',
  project: 'A project within a program',
  milestone: 'A key date or achievement',
  risk_register: 'A register of project risks',
  change_request: 'A request to change project scope',
  deliverable: 'A deliverable from a project',
  resource_allocation: 'An allocation of team capacity',
  status_report: 'A project status report',
  // Marketing
  marketing_strategy: 'A marketing strategy',
  marketing_channel: 'A marketing channel',
  marketing_campaign_plan: 'A marketing campaign plan',
  email_sequence: 'An email nurture sequence',
  social_post: 'A social media post',
  seo_keyword: 'An SEO keyword target',
  ad_creative: 'An ad creative',
  press_release: 'A press release',
  event: 'A marketing event',
  community_initiative: 'A community engagement initiative',
  // Localisation
  locale: 'A supported locale (language + region)',
  translation_key: 'A translatable string key',
  translation_bundle: 'A bundle of translation keys',
  locale_config: 'Per-locale configuration settings',
  cultural_adaptation: 'A region-specific cultural adaptation',
  regional_pricing: 'Location-based pricing',
  // Customer Education
  education_program: 'A customer education program',
  tutorial: 'A step-by-step tutorial',
  walkthrough: 'A guided product walkthrough',
  webinar: 'A live or recorded webinar',
  certification: 'A certification program',
  help_video: 'A help video',
  learning_path: 'A structured learning path',
  // Partners & Ecosystem
  partner_program: 'A partner program',
  partner_tier: 'A partner tier level',
  api_ecosystem: 'An API ecosystem',
  marketplace_listing: 'A marketplace listing',
  developer_portal: 'A developer portal',
  integration_partner: 'An integration partner',
  partner_revenue_share: 'A partner revenue sharing arrangement',
  // Foundations
  operating_lifecycle: 'A canonical, ordered (often cyclic) operating process a product\'s journey phases map onto',
  operating_stage: 'One ordered stage of an operating_lifecycle',
  specification: 'A canonical open standard or specification that primitives and products conform to',
  primitive: 'A canonical reusable building block defined by a specification',
  // Workspace
  workspace: 'A spatial thinking space for arranging entities',
  framework_exercise: 'A recorded run of a product framework (RICE, OST, and the like) over specific entities, holding its inputs and outputs',
  composition: 'A published arrangement of graph entities that has an identity of its own: a gallery, a board, a deck. Membership can be derived by running its query, position is authored by a person, and both are stored because both are real.',
  capture: 'A dated, hashed rendition of something already in the graph: a screenshot of a surface, a PDF of a report, an export of a canvas. The subject stays a graph node; the capture records what it looked like at one moment, and the content hash says whether it still does.',
})

/**
 * The authored description for an entity type, or `undefined`.
 *
 * A lookup rather than direct indexing so a caller handed a runtime string does
 * not have to assert the key exists.
 */
export function getEntityDescription(type: string): string | undefined {
  return UPG_ENTITY_DESCRIPTIONS[type]
}
